import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Menu, X } from 'lucide-react';
import { useCreditBalance } from '@dopomogai/agent-kit';
import { Button, SelectField, DropdownOption, Logo, LogoType, LogoVariant as LogoBrandVariant } from '@dopomogai/ui';
import { CreditBalanceIndicator } from '../../platform/user/CreditBalanceIndicator';
import type { CmsComponentDocument } from '@dopomogai/types';

interface NavItem {
  id: string;
  textKey: string;
  path: string;
}

interface CtaAction {
  type: string;
  payload?: any;
}

interface CtaItem {
  id: string;
  textKey: string;
  path?: string;
  action?: CtaAction;
  variant: 'ghost' | 'primary' | 'secondary' | 'outline';
}

export interface AppHeaderProps {
  onAction: (actionType: string, payload?: any) => void;
  data?: CmsComponentDocument & {
    isAuthenticated: boolean;
    user: { user_metadata?: { name?: string }, email?: string } | null;
    onSignOut: () => void;
  };
  logoBrandVariant?: LogoBrandVariant;
  logoDisplayType?: LogoType;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  onAction,
  data,
  logoBrandVariant,
  logoDisplayType = 'logo+icon'
}) => {
  const { i18n } = useTranslation('header');
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuAction, setMenuAction] = useState('');
  const [condensed, setCondensed] = useState(false);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const { balance, isLoading: isBalanceLoading } = useCreditBalance(!!data?.isAuthenticated);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.key, location.pathname, location.search, location.hash]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // --- Fallback Render (No Data) ---
  if (!data || !data.structure || !data.locales) {
    return (
      <header className="sticky top-0 z-40 px-3 pb-2 pt-3 md:px-4">
        <div className="mx-auto max-w-6xl rounded-[1.75rem] border border-slate-900/10 bg-white/90 px-4 py-3 shadow-[0_20px_48px_-28px_rgba(15,23,42,0.35),0_10px_24px_-18px_rgba(15,23,42,0.14)] backdrop-blur-xl md:px-6">
          <Logo
            to="/"
            size="md"
            theme="light"
            variant={logoBrandVariant}
            logoType={logoDisplayType}
          />
        </div>
      </header>
    );
  }

  const t = (key: string) => {
    const textProps = data.locales[i18n.language]?.props || data.locales['en']?.props || {};
    return textProps[key] || key;
  };
  const structuralProps = data.structure.root.props;
  const navItems: NavItem[] = structuralProps.navItems || [];
  const ctaItems: CtaItem[] = structuralProps.ctaItems || [];

  const handleAction = (item: CtaItem) => {
    if (item.action) onAction(item.action.type, item.action.payload);
    else if (item.path) onAction('NAVIGATE_TO_PAGE', { path: item.path });
  };

  const userMenuOptions: DropdownOption[] = [
    { value: 'dashboard', label: t('userMenu.dashboard') },
    { value: 'buy-credits', label: t('userMenu.buyCredits') },
    { value: 'signout', label: t('userMenu.signOut') },
  ];

  const handleMenuAction = (selectedValue: string | number) => {
    setMenuAction(selectedValue as string);
    switch (selectedValue) {
      case 'dashboard': navigate('/app/dashboard'); break;
      case 'buy-credits': navigate('/app/buy-credits'); break;
      case 'signout': data.onSignOut(); break;
    }
    setTimeout(() => setMenuAction(''), 100);
  };

  const isNavItemActive = (item: NavItem) => {
    if (item.path === '/blog') return location.pathname.startsWith('/blog');

    if (item.path.includes('#')) {
      const [targetPath, targetHash] = item.path.split('#');
      const normalizedPath = targetPath || '/';
      return location.pathname === normalizedPath && location.hash === `#${targetHash}`;
    }

    return location.pathname === item.path;
  };

  const NavLinkButton: React.FC<{ item: NavItem }> = ({ item }) => {
    const isActive = isNavItemActive(item);
    return (
      <button
        type="button"
        className={clsx(
          'inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',
          isActive
            ? 'border-slate-900 bg-slate-900 text-white shadow-[0_10px_22px_-16px_rgba(15,23,42,0.55)]'
            : 'border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-950'
        )}
        onClick={(e) => {
          e.preventDefault();
          onAction('NAVIGATE_TO_PAGE', { path: item.path });
        }}
      >
        {t(item.textKey)}
      </button>
    );
  };

  const renderDesktopCtas = () => {
    if (data.isAuthenticated) {
      const userName = data.user?.user_metadata?.name || data.user?.email?.split('@')[0] || t('userMenu.placeholder');
      return (
        <div className="flex items-center gap-3 pr-3 mr-1 border-r border-black/10">
          <CreditBalanceIndicator balance={balance ?? 0} isLoading={isBalanceLoading} />
          <div className="w-48">
            <SelectField
              name="user-menu"
              value={menuAction}
              onChange={handleMenuAction}
              options={userMenuOptions}
              placeholder={userName}
              triggerClassName="!py-2"
            />
          </div>
        </div>
      );
    }
    return (
      <>
        {ctaItems.map(item => (
          <Button
            key={item.id}
            size="sm"
            text={t(item.textKey)}
            variant={item.variant}
            className="rounded-full border-slate-900/10 px-5 shadow-[0_16px_30px_-22px_rgba(15,23,42,0.45)]"
            onClick={() => handleAction(item)}
          />
        ))}
      </>
    );
  };

  const renderMobileCtas = () => {
    if (data.isAuthenticated) {
      return (
        <Button
          text={t('userMenu.signOut')}
          variant="danger"
          fullWidth
          onClick={() => { closeMobileMenu(); data.onSignOut(); }}
          />
      );
    }
    return ctaItems.map(item => (
      <Button
        key={item.id}
        text={t(item.textKey)}
        variant={item.variant}
        fullWidth
        className="rounded-full"
        onClick={() => {
          closeMobileMenu();
          handleAction(item);
        }}
      />
    ));
  };

  return (
    <>
      <motion.header
        initial={false}
        animate={{
          paddingTop: condensed ? 8 : 14,
          paddingBottom: condensed ? 8 : 10,
        }}
        transition={{ type: 'tween', duration: 0.2 }}
        className="sticky top-0 z-40 px-3 md:px-4"
      >
        <motion.div
          initial={false}
          animate={{
            borderRadius: condensed ? 24 : 30,
            boxShadow: condensed
              ? '0 18px 40px -28px rgba(15,23,42,0.38), 0 8px 20px -16px rgba(15,23,42,0.16)'
              : '0 24px 54px -32px rgba(15,23,42,0.36), 0 12px 28px -20px rgba(15,23,42,0.12)',
          }}
          transition={{ type: 'tween', duration: 0.2 }}
          className="mx-auto max-w-6xl border border-slate-900/10 bg-white/90 backdrop-blur-xl"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">

            <Logo
              to="/"
              size="md"
              theme="light"
              variant={logoBrandVariant}
              logoType={logoDisplayType}
            />

            <nav className="hidden items-center rounded-full border border-slate-200 bg-slate-50/85 p-1 lg:flex">
              {navItems.map(item => <NavLinkButton key={item.id} item={item} />)}
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden lg:flex items-center gap-3">
                {renderDesktopCtas()}
              </div>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(true)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-900 shadow-[0_12px_24px_-18px_rgba(15,23,42,0.4)] transition-colors hover:bg-white lg:hidden"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-slate-950/30 backdrop-blur-sm" onClick={closeMobileMenu} />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
              className="absolute bottom-3 right-3 top-3 z-10 flex h-auto w-[min(24rem,calc(100%-1.5rem))] max-w-sm flex-col rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.45)]"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <span className="text-lg font-bold text-slate-900">Menu</span>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-700 transition-colors hover:bg-white"
                  aria-label="Close menu"
                  onClick={closeMobileMenu}
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="flex-grow space-y-2 px-4 py-5">
                {data.isAuthenticated && (
                  <>
                    <Link
                      to="/app/dashboard"
                      onClick={closeMobileMenu}
                      className="block w-full rounded-2xl border border-transparent px-4 py-3 text-left text-lg font-semibold text-gray-800 hover:border-slate-200 hover:bg-slate-50"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/app/buy-credits"
                      onClick={closeMobileMenu}
                      className="block w-full rounded-2xl border border-transparent px-4 py-3 text-left text-lg font-semibold text-gray-800 hover:border-slate-200 hover:bg-slate-50"
                    >
                      {t('userMenu.buyCredits')}
                    </Link>
                  </>
                )}
                {navItems.map(item => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={clsx(
                      'block w-full rounded-2xl border px-4 py-3 text-left text-lg font-semibold transition-colors',
                      isNavItemActive(item)
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-transparent text-gray-800 hover:border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    {t(item.textKey)}
                  </Link>
                ))}
              </nav>

              <div className="space-y-2 border-t border-slate-100 px-4 py-4">
                {renderMobileCtas()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
